import type { SoftphoneHeldCallsProps } from './index.types';
import React from 'react';
import { HeldCallItem } from './components/HeldCallItem';
import { HeldCallsCollapsible } from './components/HeldCallsCollapsible';
import { useHeldCallsLogic } from './hooks/useHeldCallsLogic';

/**
 * SoftphoneHeldCalls - Container component for displaying held calls
 *
 * Orchestrates the display of held calls:
 * - Single call: Shows HeldCallItem directly
 * - Multiple calls: Shows HeldCallsCollapsible with expandable dropdown
 *
 * Uses useHeldCallsLogic hook for state management and business logic.
 */
export const SoftphoneHeldCalls = ({
  holdingCalls,
  currentCall,
  onSwapCall,
  showSwapIcon = true,
  nonSwappableCallId,
  className = '',
  boundaryRef,
  testID,
}: SoftphoneHeldCallsProps) => {
  const {
    sortedCalls,
    isSingleCall,
    isMultipleCalls,
    isExpanded,
    handleSwapCall,
    handleToggle,
    closeCollapsible,
  } = useHeldCallsLogic({ holdingCalls, currentCall, onSwapCall });

  if (sortedCalls.length === 0) {
    return null;
  }

  const componentTestId = testID ? `${testID}-held-calls` : 'held-calls';

  return (
    <div
      role="group"
      aria-label="Calls on hold"
      className="relative w-full z-[52]"
      data-testid={componentTestId}
    >
      <div className={`bg-muted ${className}`}>
        {isSingleCall && (
          <HeldCallItem
            call={sortedCalls[0]}
            onClick={handleSwapCall}
            showTimer={true}
            showSwapIcon={showSwapIcon && sortedCalls[0].id !== nonSwappableCallId}
            isTriggerElement={true}
            testID={componentTestId}
          />
        )}

        {isMultipleCalls && (
          <HeldCallsCollapsible
            calls={sortedCalls}
            isExpanded={isExpanded}
            onToggle={handleToggle}
            closeCollapsible={closeCollapsible}
            onCallClick={handleSwapCall}
            nonSwappableCallId={nonSwappableCallId}
            boundaryRef={boundaryRef}
            testID={componentTestId}
          />
        )}
      </div>
    </div>
  );
};

SoftphoneHeldCalls.displayName = 'SoftphoneHeldCalls';
